import type { SignalChunk } from "@musejs/core";

export interface ScopeOptions {
  /** Seconds of history the paper holds. */
  readonly windowSeconds: number;
  /** Channels to plot, in order. Others in the chunk are ignored. */
  readonly channels: readonly string[];
  /** Fixed half-range for the vertical axis, or `"auto"` to track the signal. */
  readonly range: number | "auto";
  /**
   * Plot deviation from the window mean rather than the raw value.
   *
   * Needed for the optical channels, where the interesting signal is a
   * modulation of well under one part in a thousand riding on a large constant
   * offset. Plotted raw, a pulse that is plainly there renders as a flat line.
   * This is what AC coupling does on a physical recorder.
   */
  readonly removeDc?: boolean;
}

interface Pen {
  name: string;
  values: Float32Array;
  head: number;
  filled: number;
}

const CSS = getComputedStyle(document.documentElement);
const token = (name: string): string => CSS.getPropertyValue(name).trim();

/**
 * A strip-chart recorder on a canvas.
 *
 * Each channel gets its own lane rather than being overlaid, which is how a
 * paper recorder works and how a technician reads one: a channel that goes bad
 * is obvious because its lane goes bad, not because one trace in a thicket
 * changed shape.
 *
 * The right-hand margin is the signature: it is the chunk's own
 * `timing.uncertaintyUs` drawn to the same time scale as the trace. That number
 * is 20–40 ms on this hardware, and drawing it means the plot never implies
 * timing it does not have.
 */
export class Scope {
  readonly #canvas: HTMLCanvasElement;
  readonly #context: CanvasRenderingContext2D;
  readonly #options: ScopeOptions;
  readonly #pens: Pen[] = [];
  #capacity = 0;
  #sampleRate = 0;
  #uncertaintyUs = 0;
  #scale = 1;

  constructor(canvas: HTMLCanvasElement, options: ScopeOptions) {
    this.#canvas = canvas;
    const context = canvas.getContext("2d");
    if (context === null) throw new Error("Canvas 2D context unavailable");
    this.#context = context;
    this.#options = options;
    this.#resize();
    new ResizeObserver(() => this.#resize()).observe(canvas);
  }

  get sampleRate(): number {
    return this.#sampleRate;
  }

  /** Most recent value per channel, for the numeric readouts beside the strip. */
  latest(): Map<string, number> {
    const out = new Map<string, number>();
    for (const pen of this.#pens) {
      if (pen.filled === 0) continue;
      const index = (pen.head - 1 + pen.values.length) % pen.values.length;
      out.set(pen.name, pen.values[index] as number);
    }
    return out;
  }

  accept(chunk: SignalChunk): void {
    if (chunk.sampleRate !== this.#sampleRate) {
      this.#sampleRate = chunk.sampleRate;
      this.#capacity = Math.max(2, Math.round(chunk.sampleRate * this.#options.windowSeconds));
      this.#pens.length = 0;
    }
    this.#uncertaintyUs = chunk.timing.uncertaintyUs;

    for (const name of this.#options.channels) {
      const column = chunk.channels[name];
      if (column === undefined) continue;
      let pen = this.#pens.find((p) => p.name === name);
      if (pen === undefined) {
        pen = { name, values: new Float32Array(this.#capacity), head: 0, filled: 0 };
        this.#pens.push(pen);
      }
      for (const value of column) {
        pen.values[pen.head] = value;
        pen.head = (pen.head + 1) % pen.values.length;
        if (pen.filled < pen.values.length) pen.filled++;
      }
    }
  }

  clear(): void {
    this.#pens.length = 0;
    this.#sampleRate = 0;
  }

  #resize(): void {
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const rect = this.#canvas.getBoundingClientRect();
    this.#canvas.width = Math.max(1, Math.round(rect.width * ratio));
    this.#canvas.height = Math.max(1, Math.round(rect.height * ratio));
    this.#scale = ratio;
  }

  draw(): void {
    const ctx = this.#context;
    const width = this.#canvas.width;
    const height = this.#canvas.height;
    const s = this.#scale;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, width, height);
    this.#drawGrid(width, height, s);
    if (this.#pens.length === 0) return;

    const laneHeight = height / this.#pens.length;
    for (const [index, pen] of this.#pens.entries()) {
      this.#drawPen(pen, index * laneHeight, laneHeight, width, s);
    }
    this.#drawUncertainty(width, height, s);
  }

  #drawGrid(width: number, height: number, s: number): void {
    const ctx = this.#context;
    // A chart recorder's grid is time-referenced, so the spacing means
    // something: one minor division is a tenth of the window.
    const minor = width / (this.#options.windowSeconds * 5);
    ctx.lineWidth = s;
    for (let x = 0, n = 0; x <= width + 1; x += minor, n++) {
      ctx.strokeStyle = n % 5 === 0 ? token("--grid-major") : token("--grid");
      ctx.beginPath();
      ctx.moveTo(Math.round(x) + 0.5, 0);
      ctx.lineTo(Math.round(x) + 0.5, height);
      ctx.stroke();
    }
    const rows = 4;
    ctx.strokeStyle = token("--grid");
    for (let i = 1; i < rows; i++) {
      const y = Math.round((height * i) / rows) + 0.5;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }
  }

  #drawPen(pen: Pen, top: number, laneHeight: number, width: number, s: number): void {
    if (pen.filled < 2) return;
    const ctx = this.#context;
    const midline = top + laneHeight / 2;
    const usable = (laneHeight / 2) * 0.86;

    let centre = 0;
    if (this.#options.removeDc === true) {
      for (let i = 0; i < pen.filled; i++) centre += pen.values[i] as number;
      centre /= pen.filled;
    }

    let range: number;
    if (this.#options.range === "auto") {
      let peak = 0;
      for (let i = 0; i < pen.filled; i++) {
        const v = Math.abs((pen.values[i] as number) - centre);
        if (v > peak) peak = v;
      }
      // A floor stops a silent channel from being amplified into pure noise.
      range = Math.max(peak, 1e-9);
    } else {
      range = this.#options.range;
    }

    ctx.strokeStyle = token("--trace");
    ctx.lineWidth = 1.25 * s;
    ctx.lineJoin = "round";
    ctx.beginPath();

    const start = (pen.head - pen.filled + pen.values.length) % pen.values.length;
    const step = width / Math.max(1, this.#capacity - 1);
    const offset = (this.#capacity - pen.filled) * step;

    for (let i = 0; i < pen.filled; i++) {
      const value = (pen.values[(start + i) % pen.values.length] as number) - centre;
      const x = offset + i * step;
      const y = midline - Math.max(-1, Math.min(1, value / range)) * usable;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();

    // Lane separator, drawn after the trace so it reads as paper, not as signal.
    ctx.strokeStyle = token("--paper-edge");
    ctx.lineWidth = s;
    ctx.beginPath();
    ctx.moveTo(0, Math.round(top) + 0.5);
    ctx.lineTo(width, Math.round(top) + 0.5);
    ctx.stroke();
  }

  /**
   * Draws the timestamp uncertainty at the leading edge, to the time scale of
   * the trace beside it.
   */
  #drawUncertainty(width: number, height: number, s: number): void {
    if (this.#uncertaintyUs <= 0) return;
    const pixelsPerSecond = width / this.#options.windowSeconds;
    const span = Math.max(1.5 * s, (this.#uncertaintyUs / 1e6) * pixelsPerSecond);

    const ctx = this.#context;
    ctx.fillStyle = token("--flag");
    ctx.globalAlpha = 0.14;
    ctx.fillRect(width - span, 0, span, height);
    ctx.globalAlpha = 1;
    ctx.strokeStyle = token("--flag");
    ctx.lineWidth = s;
    ctx.beginPath();
    ctx.moveTo(Math.round(width - span) + 0.5, 0);
    ctx.lineTo(Math.round(width - span) + 0.5, height);
    ctx.stroke();
  }
}
